import { responseDataToCamelCase } from "@shared/v2/caseTransformingAxios";
import { isEmpty, startCase } from "lodash";

const IS_REQUIRED = "is a required field.";

const validateBirthdate = (detail, birthdate) => {
  if (!birthdate) return;
  const [year, month, day] = birthdate.split("-");
  if (!month) detail.birthdate = "Cannot save without a month";
  if (!day) detail.birthdate = "Cannot save without a day";
  if (year && !month && !day) detail.birthdate = "Cannot save without a month and day";
};

export const validateLeadForm = (person) => {
  const detail = {};
  if (!(person.firstName || "").trim()) detail["First Name"] = IS_REQUIRED;
  if (!(person.lastName || "").trim()) detail["Last Name"] = IS_REQUIRED;
  if (!person.sourceId) detail.Source = IS_REQUIRED;
  validateBirthdate(detail, person.milestones?.birthdate);

  return { errors: isEmpty(detail) ? [] : [{ detail }] };
};

export const validateWebsiteProfileForm = (person) => {
  const detail = {};
  if (!(person.firstName || "").trim()) detail["First Name"] = IS_REQUIRED;
  if (!(person.lastName || "").trim()) detail["Last Name"] = IS_REQUIRED;
  validateBirthdate(detail, person.milestones?.birthdate);
  if (!(person.emailDetailsAttributes?.find((e) => e.position === 1)?.value || "").trim()) {
    detail.Email = IS_REQUIRED;
  }

  return { errors: isEmpty(detail) ? [] : [{ detail }] };
};

export const validateUserForm = (person) => {
  const detail = {};
  const isOperator = person.displayedPosition === "Operator";
  if (!(person.firstName || "").trim()) detail["First Name"] = IS_REQUIRED;
  if (!(person.lastName || "").trim()) detail["Last Name"] = IS_REQUIRED;
  if (!(person.milestones?.organization_start_date || "").trim()) detail["Start Date at Team"] = IS_REQUIRED;
  validateBirthdate(detail, person.milestones?.birthdate);
  if (!(person.emailDetailsAttributes?.find((e) => e.position === 1)?.value || "").trim()) {
    detail.Email = IS_REQUIRED;
  }
  if (!person.position && !isOperator) detail.Position = IS_REQUIRED;
  if (!person.agentStatus) detail["Production Status"] = IS_REQUIRED;
  if (!person.id && typeof person.loginUserAttributes?.inviteToOkta === "undefined") {
    detail["Invite User selection"] = IS_REQUIRED;
  }
  if (person.isRealEstateLlc && !person.individualRealEstateLlcName) {
    detail["Real Estate LLC Name"] = IS_REQUIRED;
  }

  person.loginUserAttributes?.customLinksAttributes?.forEach((customLink) => {
    if (!customLink.name) detail["Custom Link Title"] = "is missing";
    if (!customLink.value) detail["Custom Link URL"] = "is missing";
  });

  return { errors: isEmpty(detail) ? [] : [{ detail }] };
};

export const validateForm = (person, modalType) => {
  if (modalType === "lead") return validateLeadForm(person);
  if (modalType === "websiteProfile" || modalType === "lender") return validateWebsiteProfileForm(person);
  return validateUserForm(person);
};

export const transformFormOptions = (formOptions) => ({
  ...formOptions,
  brokerageOptions: formOptions.brokerageOptions?.map((option) => ({
    label: option,
    value: option,
  })),
  militaryStatusOptions: formOptions.militaryStatusOptions?.map((option) => ({
    label: option,
    value: option,
  })),
  positionOptions: formOptions.positionOptions?.map((option) => ({
    label: option,
    value: option,
  })),
  mlsOptions: formOptions.mlsOptions?.map((option) => ({
    label: option.name,
    value: option.id,
    description: option.description,
  })),
  maritalStatusOptions: formOptions.maritalStatusOptions.map((option) => ({
    label: option.displayName || option,
    value: option.value || option,
  })),
  militaryStatusOptions: formOptions.militaryStatusOptions?.map((option) => ({
    label: option,
    value: option,
  })),
  tagsOptions: formOptions.tagsOptions.map((option) => ({
    label: option.name,
    value: option.name,
  })),
  timezoneOptions: formOptions.timezoneOptions.map((option) => ({
    label: option.displayName,
    value: option.value,
  })),
  stateOptions: Object.entries(formOptions.stateOptions).map(([category, options]) => ({
    category: startCase(category),
    options: options.map(({ name, code }) => ({
      label: `${name} (${code})`,
      value: code,
    })),
  })),
  primaryAgentOptions: formOptions.primaryAgentOptions?.map((option) => ({
    label: option.fullName,
    value: option.id,
  })),
  statusOptions: formOptions.statusOptions?.map((option) => ({
    label: option.displayName,
    value: option.value,
  })),
  customDateOptions: formOptions.milestonesOptions
    .reduce((result, item) => {
      if (!item.default) result.push(item);
      return result;
    }, [])
    .sort((a, b) => a.displayName.localeCompare(b.displayName)),
  leadStageOptions: (formOptions.stageOptions || []).map((option) => ({
    label: option,
    value: option,
    type: "lead",
  })),
  candidateStageOptions: (formOptions.candidateStageOptions || []).map((option) => ({
    label: option,
    value: option,
    type: "candidate",
  })),
  leadTypeOptions: (formOptions.leadTypeOptions || []).map((option) => ({
    label: option.displayName,
    value: option.value,
  })),
  typeNameOptions: formOptions.typeNameOptions?.map((option) => ({
    label: option.displayName,
    value: option.value,
  })),
});

export const transformSources = (sources) =>
  sources.map((source) => ({
    label: source.text,
    value: source.id,
  }));

const transformLoginUser = (loginUser) => {
  if (!loginUser) return null;
  const { customLinks, usersMlses, ...loginUserAttributes } = loginUser;
  return {
    customLinksAttributes: customLinks,
    usersMlsesAttributes: usersMlses,
    ...loginUserAttributes,
  };
};

export const transformPerson = (rawPerson) => {
  if (!rawPerson) return null;
  const {
    addresses,
    personBio,
    emailDetails,
    loginUser,
    milestones,
    phoneDetails,
    websiteDisplayPhoneDetails,
    ...person
  } = responseDataToCamelCase(rawPerson);
  return {
    addressesAttributes: addresses,
    personBioAttributes: personBio,
    emailDetailsAttributes: emailDetails,
    loginUserAttributes: transformLoginUser(loginUser),
    phoneDetailsAttributes: phoneDetails,
    websiteDisplayPhoneDetailsAttributes: websiteDisplayPhoneDetails,
    milestones: rawPerson.milestones,
    ...person,
  };
};
